import { useRead, ReadState, isCollection } from "@/components/LoadingStates";
import { useEffect, useId, useRef, useState } from "react";
import api, { formatError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useRealtime } from "@/context/RealtimeContext";
import PresenceDot, { PRESENCE_SCOPE } from "@/components/PresenceDot";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Copy, RefreshCw, Trash2, Mail, Loader2, X, Link2 } from "lucide-react";
import { timeAgo } from "@/lib/format";
import ActivityFeed from "@/components/ActivityFeed";
import RoleBadge, { ROLE_META } from "@/components/RoleBadge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

// OP-037: invite delivery states as recorded by the server; "sent" means the provider accepted the request.
const INVITE_DELIVERY = { sent: "accepté par le fournisseur", failed: "échec d’envoi", pending_provider: "e-mail non envoyé" };

// `label` says whose role it is; the current value stays in the name since aria-label replaces the trigger text.
function RoleSelect({ value, onChange, testid, className, label }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className={className || "h-8 w-32 text-xs"} aria-label={`${label}: ${ROLE_META[value]?.label || value}`} data-testid={testid}><SelectValue /></SelectTrigger>
      <SelectContent>
        {Object.entries(ROLE_META).map(([k, m]) => <SelectItem key={k} value={k} data-testid={`${testid}-option-${k}`}>{m.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

export default function WorkspacePanel({ id, onChanged, onDeleted }) {
  const { user, refresh } = useAuth();
  const { onlineIds } = useRealtime();
  const workspaceRead = useRead(`/workspaces/${id}`, (d) => d && typeof d.id === "string" && isCollection(d.members) && isCollection(d.pending_invites) && d.counts);
  const ws = workspaceRead.data;
  const [email, setEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("editor");
  const [inviting, setInviting] = useState(false);
  const [lastLink, setLastLink] = useState("");
  const emailId = useId();
  const linkId = useId();

  const load = () => workspaceRead.reload().catch(() => {});
  useEffect(() => { setLastLink(""); }, [workspaceRead.key]);
  const reloadRead = workspaceRead.reload;
  const priorOnline = useRef(onlineIds);
  useEffect(() => { if (priorOnline.current !== onlineIds) { priorOnline.current = onlineIds; reloadRead().catch(() => {}); } }, [onlineIds, reloadRead]);
  if (workspaceRead.status !== "success") return <ReadState read={workspaceRead} scope="workspace-panel" />;

  const copy = async (text, msg) => {
    try { await navigator.clipboard.writeText(text); } catch { /* ignore */ }
    toast.success(msg);
  };

  const invite = async (e) => {
    if (!ws?.is_owner || workspaceRead.status !== "success") return;
    e.preventDefault();
    setInviting(true);
    try {
      const { data } = await api.post(`/workspaces/${id}/invite`, { email, role: inviteRole });
      setLastLink(data.link);
      if (data.delivery.status === "sent") toast.success(`Invitation acceptée par le fournisseur pour ${email} ; réception non confirmée. Le lien ci-dessous reste utilisable.`);
      else if (data.delivery.status === "failed") toast.error(`Email failed (${data.delivery.error}). Share the link manually instead.`);
      else toast.success("Invite created — share the link below");
      setEmail("");
      load();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setInviting(false);
    }
  };

  const regen = async () => {
    if (!ws?.is_owner || workspaceRead.status !== "success") return;
    try {
      await api.post(`/workspaces/${id}/regenerate-code`);
      toast.success("New invite code generated");
      load();
    } catch (error) { toast.error(formatError(error)); }
  };

  const removeMember = async (mid) => {
    if (!ws || (mid !== user.id && !ws.is_owner) || (ws.members.find((m) => m.id === mid)?.role === "owner" && ws.owner_count <= 1)) return;
    try {
      await api.delete(`/workspaces/${id}/members/${mid}`);
      toast.success(mid === user.id ? "You left the workspace" : "Member removed");
      if (mid === user.id) onDeleted(); else { load(); onChanged(); }
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  const changeRole = async (mid, role) => {
    if (!ws?.is_owner || workspaceRead.status !== "success") return;
    try {
      const { data } = await api.patch(`/workspaces/${id}/members/${mid}/role`, { role });
      toast.success(`Role updated to ${ROLE_META[data.role].label}`);
      await load();
      if (mid === user.id) await refresh();
      onChanged();
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  const revoke = async (invId) => {
    if (!ws?.is_owner || workspaceRead.status !== "success") return;
    try { await api.delete(`/workspaces/${id}/invites/${invId}`); load(); }
    catch (error) { toast.error(formatError(error)); }
  };

  const del = async () => {
    if (!ws?.is_owner || workspaceRead.status !== "success") return;
    if (!window.confirm(`Delete "${ws.name}" and all its shared watchlists, saved videos and swipes?`)) return;
    try {
      await api.delete(`/workspaces/${id}`);
      toast.success("Workspace deleted");
      onDeleted();
    } catch (error) { toast.error(formatError(error)); }
  };

  return (
    <ReadState read={workspaceRead} scope="workspace-panel">
    <div className="space-y-4" data-testid="workspace-panel">
      <Card className="p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h2 className="font-display text-2xl font-extrabold" data-testid="workspace-panel-name">{ws.name}</h2>
            <p className="text-xs text-muted-foreground mt-1">
              {ws.members.length} member{ws.members.length !== 1 ? "s" : ""} · {ws.counts.watchlists} watchlists · {ws.counts.saved} saved videos · {ws.counts.swipes} swipes
            </p>
          </div>
          {ws.is_owner ? (
            <div className="flex items-center gap-2">
              <RoleBadge role="owner" testid="panel-my-role" />
              {!(ws.owner_count <= 1) && <Button variant="outline" size="sm" onClick={() => removeMember(user.id)} data-testid="workspace-leave-button">Leave</Button>}
              <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-[hsl(var(--rose))]" onClick={del} data-testid="workspace-delete-button"><Trash2 className="h-4 w-4 mr-1" /> Delete</Button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <RoleBadge role={ws.my_role} testid="panel-my-role" />
              <Button variant="outline" size="sm" onClick={() => removeMember(user.id)} data-testid="workspace-leave-button">Leave workspace</Button>
            </div>
          )}
        </div>
      </Card>

      {ws.is_owner && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="p-5" data-testid="invite-code-card">
            <div className="text-xs uppercase tracking-wider text-muted-foreground font-medium mb-2 flex items-center gap-1.5"><Link2 className="h-3.5 w-3.5" /> Invite code</div>
            <div className="flex items-center gap-2">
              <div className="font-mono text-2xl font-bold tracking-widest flex-1" data-testid="invite-code">{ws.invite_code}</div>
              <Button variant="outline" size="icon" onClick={() => copy(ws.invite_code, "Code copied")} aria-label="Copy code" data-testid="copy-invite-code"><Copy className="h-4 w-4" /></Button>
              <Button variant="ghost" size="icon" onClick={regen} aria-label="Regenerate invite code" title="Regenerate invite code" data-testid="regenerate-invite-code"><RefreshCw className="h-4 w-4" /></Button>
            </div>
            <p className="text-xs text-muted-foreground mt-2">Teammates paste this on the Team page (Join with a code). Regenerate to revoke.</p>
          </Card>

          <Card className="p-5" data-testid="invite-email-card">
            <div className="text-xs uppercase tracking-wider text-muted-foreground font-medium mb-2 flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" /> Invite by email</div>
            <form onSubmit={invite} className="flex flex-col sm:flex-row gap-2">
              <label htmlFor={emailId} className="sr-only">Teammate email</label>
              <Input id={emailId} type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="teammate@company.com" required className="flex-1" data-testid="invite-email-input" />
              <RoleSelect value={inviteRole} onChange={setInviteRole} label="Invitation role" testid="invite-role-select" className="h-10 sm:w-28 text-xs" />
              <Button type="submit" disabled={inviting || !email} data-testid="invite-email-button">{inviting ? <><Loader2 className="h-4 w-4 animate-spin" /><span className="sr-only">Sending invitation…</span></> : <>Send<span className="sr-only"> invitation</span></>}</Button>
            </form>
            {lastLink && (
              <div className="mt-3 flex items-center gap-2">
                <label htmlFor={linkId} className="sr-only">Invitation link</label>
                <Input id={linkId} readOnly value={lastLink} className="font-mono text-[11px]" data-testid="invite-link-input" />
                <Button variant="outline" size="icon" onClick={() => copy(lastLink, "Invite link copied")} aria-label="Copy link" data-testid="copy-invite-link"><Copy className="h-4 w-4" /></Button>
              </div>
            )}
            <p className="text-xs text-muted-foreground mt-2">Envoi tenté via Resend seulement si une clé est configurée ; la réception n’est jamais confirmée ici (le bac à sable ne livre qu’à l’adresse du compte Resend). Un lien d’invitation est toujours affiché pour un partage manuel.</p>
          </Card>
        </div>
      )}

      <Card className="p-5" data-testid="members-card">
        <h3 className="font-display font-bold text-lg mb-1">Members</h3>
        <p className="text-xs text-muted-foreground mb-3" data-testid="presence-scope-note">{PRESENCE_SCOPE}</p>
        <div className="divide-y divide-border">
          {ws.members.map((m) => (
            <div key={m.id} className="flex items-center gap-3 py-2.5" data-testid={`member-row-${m.id}`}>
              <div className="relative flex h-8 w-8 items-center justify-center rounded-full bg-primary/15 text-primary font-semibold text-xs">
                {m.name?.[0]?.toUpperCase() || "U"}
                <PresenceDot online={onlineIds.includes(m.id)} testid={`presence-dot-${m.id}`} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{m.name}{m.id === user.id && <span className="text-muted-foreground font-normal"> (you)</span>}</div>
                <div className="text-xs text-muted-foreground truncate">{m.email}</div>
              </div>
              {onlineIds.includes(m.id) ? (
                <span className="hidden sm:inline-flex items-center gap-1.5 text-[11px] font-medium text-[hsl(var(--emerald))]" data-testid={`member-online-${m.id}`}>
                  <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--emerald))]" /> Dans l’app
                </span>
              ) : m.last_seen_at ? (
                <span className="hidden sm:inline text-[11px] text-muted-foreground" data-testid={`member-last-seen-${m.id}`}>
                  active {timeAgo(m.last_seen_at)}
                </span>
              ) : null}
              {ws.is_owner ? (
                <RoleSelect value={m.role} onChange={(r) => changeRole(m.id, r)} label={`Role of ${m.name || m.email}`} testid={`member-role-select-${m.id}`} />
              ) : (
                <RoleBadge role={m.role} testid={`member-role-${m.id}`} />
              )}
              {ws.is_owner && !(m.role === "owner" && ws.owner_count <= 1) && m.id !== user.id && (
                <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-[hsl(var(--rose))]" onClick={() => removeMember(m.id)} aria-label={`Remove ${m.name || m.email} from the workspace`} title="Remove" data-testid={`remove-member-${m.id}`}><X className="h-4 w-4" /></Button>
              )}
            </div>
          ))}
        </div>
        {ws.pending_invites.length > 0 && (
          <div className="mt-4">
            <div className="text-xs uppercase tracking-wider text-muted-foreground font-medium mb-2">Pending invites</div>
            <div className="divide-y divide-border">
              {ws.pending_invites.map((inv) => (
                <div key={inv.id} className="flex items-center gap-3 py-2 text-sm" data-testid={`pending-invite-${inv.id}`}>
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <span className="flex-1 truncate">{inv.email}</span>
                  <RoleBadge role={inv.role || "editor"} />
                  <span className={`text-[10px] uppercase tracking-wider rounded-full px-2 py-0.5 ${inv.delivery?.status === "sent" ? "bg-[hsl(var(--emerald)/0.12)] text-[hsl(var(--emerald))]" : "bg-muted text-muted-foreground"}`} data-testid={`pending-invite-delivery-${inv.id}`}>{INVITE_DELIVERY[inv.delivery?.status] || inv.delivery?.status?.replace("_", " ") || "pending"}</span>
                  <span className="text-xs text-muted-foreground">{timeAgo(inv.created_at)}</span>
                  {ws.is_owner && <button type="button" onClick={() => revoke(inv.id)} className="text-muted-foreground hover:text-[hsl(var(--rose))]" aria-label={`Revoke the invitation for ${inv.email}`} title="Revoke" data-testid={`revoke-invite-${inv.id}`}><X className="h-4 w-4" /></button>}
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>

      <ActivityFeed workspaceId={id} />
    </div>
    </ReadState>
  );
}
